import React from 'react';

import { Footer, Header } from '$partials/global';
import ArticleNavBar from '$partials/articles/article-nav-bar';
import ArticleListingGrid from '$partials/articles/article-listing-grid';
import ArticleListingGridCompact from '$partials/articles/article-listing-grid-compact';
import { useAjaxListing } from '$hooks/ajax-listing';
import { AjaxListingContext, InfinitePagination } from '$partials/base';
import ArticleListingItem from '$partials/items/article-listing-item';

/* ========================================================================= */
/* Component(s) */
/* ========================================================================= */

/**
 *
 * @type {import('$types/components/pages').ArticleLandingPage}
 */
const ArticleLandingPage = (props) => {
    const page = props.data.page;

    const searchMode = !!page.items?.filters?.searchTerm?.value;

    const listing = useAjaxListing({
        infinite: true,
        items: page.items?.edges,
        path: `/api/v1/listings/${encodeURIComponent(props.data?.uri ?? '')}`,
        page: page.items?.page || 1,
        totalItems: page.items?.total,
        totalPages: page.items?.totalPages,
        query: {
            searchTerm: page.items?.filters?.searchTerm?.value
        }
    });

    return (
        <>
            <Header {...props.data.header} />

            <main id='main-content'>
                <header className='theme-light-alt bg-main-background'>
                    <div className='layout-container layout-site-outer-padding pb-24 sm:pb-40 pt-64 md:pt-160 md:pb-48'>
                        <h1 className='text-main-text-primary type-style-1'>
                            {!searchMode && page?.title}
                            {searchMode && `Results for "${page.items.filters?.searchTerm?.value}"`}
                        </h1>
                    </div>
                    <div className='layout-container layout-site-outer-padding pt-24 md:py-24'>
                        <ArticleNavBar
                            pages={[
                                {
                                    title: 'All',
                                    url: page.url,
                                },
                                ...page.listings.edges.map(e => e.node)
                            ]}
                            searchTerm={page.items?.filters?.searchTerm?.value}/>
                    </div>
                </header>

                {/* Regular listings */}
                {(page.listings?.edges?.length > 0 && !searchMode) && (
                    <div>
                        {page.listings.edges.map((edge) => {
                            const page = edge?.node;
                            if (!page || page?.items?.edges?.length === 0) {
                                return null;
                            }
                            return (
                                <>
                                    {page.landingLayout === 'FEATURE_ROW' && (
                                        <ArticleListingGrid
                                            heading={`Latest ${page.title}`}
                                            items={page.items}
                                            key={page.id}
                                            listingLink={{
                                                url: page.url,
                                                title: 'View all'
                                            }} />
                                    )}
                                    {(page.landingLayout === 'ROW' || page.landingLayout === 'GRID') && (
                                        <ArticleListingGridCompact
                                            heading={`Latest ${page.title}`}
                                            items={page.items}
                                            key={page.id}
                                            listingLink={{
                                                url: page.url,
                                                title: 'View all'
                                            }}
                                            maxItem={page.landingLayout === 'GRID' ? 6 : 3} />
                                    )}
                                </>
                            );
                        })}
                    </div>
                )}

                {/* Search page mode */}

                {searchMode && (
                    <AjaxListingContext.Provider value={listing}>
                        <div className='layout-container layout-site-outer-padding py-32 md:py-64'>
                            {listing?.items?.length > 0 && (
                                <aside>
                                    <p className='type-style-eyebrow'>
                                        {`${listing.totalItems} result${listing.totalItems !== 1 ? 's' : ''}`}
                                    </p>
                                </aside>
                            )}

                            <div className='sm:-mx-16 md:-mx-24 mt-16 md:mt-24'>
                                {listing.items?.length > 0 && (
                                    <div
                                        aria-busy={listing.loading}
                                        aria-label='Articles'
                                        className='grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-y-40'
                                        role='feed'>
                                        {listing.items?.length > 0 && (
                                            <>
                                                {listing.items.map(
                                                    /** @type {(edge: import("$types/generated/cms-graphql.d.ts").ArticleListingPageItemsOnlyFragment['items']['edges'][0], i: number) => any} */
                                                    (edge, i) => {
                                                        return (
                                                            <article
                                                                className={`sm:px-16 md:px-24 sm:border-l sm:border-l-tint-dark ${i % 3 === 0 ? 'md:border-opacity-0' : 'md:border-opacity-20'} ${i % 2 === 0 ? 'sm:border-opacity-0' : 'sm:border-opacity-20'}`}
                                                                key={edge.node.id}>
                                                                <ArticleListingItem {...edge.node} />
                                                            </article>
                                                        );
                                                    }
                                                )}
                                            </>
                                        )}
                                    </div>
                                )}
                                {/* No result */}
                                {listing.items?.length === 0 && (
                                    <div className='sm:px-16 md:px-24 mb-16 md:mb-24 type-style-3'>
                                        {'No results'}
                                    </div>
                                )}
                                <InfinitePagination>
                                    {'Loading...'}
                                </InfinitePagination>
                            </div>
                        </div>
                    </AjaxListingContext.Provider>
                )}

            </main>
            <Footer {...props.data.footer} />
        </>
    );
};

/* ========================================================================= */
/* Exports */
/* ========================================================================= */

export default ArticleLandingPage;
